import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Image as KonvaImage, Rect, Text, Transformer, Group } from 'react-konva';
import Konva from 'konva';
import { useEditorStore } from '../store/editor-store';
import { getSelectionIcons } from '../lib/selectionIcons';
import { oxAuthHeaders } from '../lib/guestId';
import { maskImageUrl } from '../lib/masks';
import { maskPhotoLayout, squareMaskBox } from '../lib/maskFit';
import { bakeImageFilter, resolveImageFilter } from '../lib/imageFilters';
import { layoutCurvedText } from '../lib/textCurve';
import { cssFontWeight } from '../lib/textMeasure';
import { aspectMismatch, fitImageToPrintArea } from '../lib/imageFit';
import type { CurveGlyph, ImageFilter, TextCurve } from '@ox/schema';

function useCurvedTextImage(opts: {
  enabled: boolean;
  glyphs: Array<{ char: string; x: number; y: number; rotation: number }>;
  width: number;
  height: number;
  fontSize: number;
  fontFamily: string;
  fontStyle: string;
  fill: string;
  /** Bust cache when content/curve/layout changes */
  cacheKey: string;
}) {
  const [img, setImg] = useState<HTMLCanvasElement | null>(null);
  const glyphsRef = useRef(opts.glyphs);
  glyphsRef.current = opts.glyphs;

  useEffect(() => {
    if (!opts.enabled || opts.width < 1 || opts.height < 1) {
      setImg(null);
      return;
    }
    const glyphs = glyphsRef.current;
    if (glyphs.length === 0) {
      setImg(null);
      return;
    }
    let cancelled = false;

    const paint = () => {
      if (cancelled) return;
      const dpr = Math.min(2, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.ceil(opts.width * dpr));
      canvas.height = Math.max(1, Math.ceil(opts.height * dpr));
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, opts.width, opts.height);
      ctx.font = `${opts.fontStyle} ${opts.fontSize}px ${opts.fontFamily}`;
      ctx.fillStyle = opts.fill || '#111111';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (const g of glyphs) {
        if (!g.char || g.char === ' ') continue;
        ctx.save();
        ctx.translate(g.x, g.y);
        ctx.rotate((g.rotation * Math.PI) / 180);
        ctx.fillText(g.char, 0, 0);
        ctx.restore();
      }
      if (!cancelled) setImg(canvas);
    };

    const fonts = typeof document !== 'undefined' ? document.fonts : null;
    if (fonts?.ready) {
      void fonts.ready.then(paint).catch(paint);
      paint();
    } else {
      paint();
    }

    return () => {
      cancelled = true;
    };
  }, [
    opts.enabled,
    opts.cacheKey,
    opts.width,
    opts.height,
    opts.fontSize,
    opts.fontFamily,
    opts.fontStyle,
    opts.fill,
  ]);

  return img;
}

function useHtmlImage(url?: string) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!url) {
      setImg(null);
      return;
    }
    const i = new window.Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => setImg(i);
    i.src = url;
  }, [url]);
  return img;
}

/** Fit mockup into the available canvas slot (no page scroll).
 *  Scales up or down so small templates fill the screen area without overflowing.
 */
function useFitStage(assetW: number, assetH: number, container: HTMLElement | null) {
  const [box, setBox] = useState({ width: 420, height: 520 });

  useEffect(() => {
    if (!container) return;
    const update = () => {
      const pad = 12;
      const w = Math.max(120, container.clientWidth - pad);
      const h = Math.max(120, container.clientHeight - pad);
      setBox({ width: w, height: h });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(container);
    return () => ro.disconnect();
  }, [container]);

  // Allow scale > 1 so small mockup assets grow to use free viewport space.
  // Cap only by the container — never crop/overflow.
  const safeW = Math.max(1, assetW);
  const safeH = Math.max(1, assetH);
  const scale = Math.min(box.width / safeW, box.height / safeH);
  return {
    width: Math.max(1, Math.round(safeW * scale)),
    height: Math.max(1, Math.round(safeH * scale)),
    /** Display px per mockup asset px */
    scale,
  };
}

/** Document stores top-left; Konva nodes use center + offset for rotation. */
function centerOf(x: number, y: number, w: number, h: number) {
  return { x: x + w / 2, y: y + h / 2 };
}

function topLeftOf(cx: number, cy: number, w: number, h: number) {
  return { x: cx - w / 2, y: cy - h / 2 };
}

const HANDLE_CSS =
  typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches ? 28 : 26;

export function DesignerCanvas() {
  const model = useEditorStore((s) => s.model);
  const document = useEditorStore((s) => s.document);
  const activeView = useEditorStore((s) => s.activeView);
  const selectedId = useEditorStore((s) => s.selectedId);
  const showGuides = useEditorStore((s) => s.showGuides);
  const previewMode = useEditorStore((s) => s.previewMode);
  const select = useEditorStore((s) => s.select);
  const updateObject = useEditorStore((s) => s.updateObject);
  const deleteSelected = useEditorStore((s) => s.deleteSelected);

  const wrapRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage | null>(null);
  const areaHostRef = useRef<HTMLElement | null>(null);
  const [, bump] = useState(0);
  const [deletePos, setDeletePos] = useState<{
    left: number;
    top: number;
    rotation: number;
  } | null>(null);
  const icons = useRef(typeof window !== 'undefined' ? getSelectionIcons(HANDLE_CSS) : null);

  useEffect(() => {
    areaHostRef.current = wrapRef.current?.parentElement ?? null;
    bump((n) => n + 1);
  }, []);

  const color = model?.colors.find((c) => c.key === document.selection.colorKey) ?? model?.colors[0];
  const mockupUrl = color?.mockups[activeView]?.url;
  const pxPerMm = color?.mockups[activeView]?.pxPerMm ?? 1.79;
  const mockup = useHtmlImage(mockupUrl);

  const assetW = mockup?.naturalWidth || 420;
  const assetH = mockup?.naturalHeight || 520;
  const stage = useFitStage(assetW, assetH, areaHostRef.current);

  const area = model?.printAreas[activeView];
  // Objects are in mm relative to the print area.
  // Print area x/y/width/height are garment mm from the calibrated template (admin/API).
  // Scale factor: mockup display px / mockup asset px, then * pxPerMm of the asset.
  const displayPxPerMm = pxPerMm * stage.scale;

  const areaX = (area?.x ?? 0) * displayPxPerMm;
  const areaY = (area?.y ?? 0) * displayPxPerMm;
  const areaW = (area?.width ?? 180) * displayPxPerMm;
  const areaH = (area?.height ?? 220) * displayPxPerMm;
  const bleedMm = 5;
  const bleed = bleedMm * displayPxPerMm;

  const objects = document.views.find((v) => v.viewId === activeView)?.objects ?? [];
  const trRef = useRef<Konva.Transformer>(null);
  const selectedRef = useRef<Konva.Node | null>(null);
  const nodeByIdRef = useRef(new Map<string, Konva.Node>());

  const registerNode = useCallback((id: string, node: Konva.Node | null) => {
    if (node) nodeByIdRef.current.set(id, node);
    else nodeByIdRef.current.delete(id);
  }, []);

  const syncDeletePos = useCallback(() => {
    const tr = trRef.current;
    const wrap = wrapRef.current;
    const stageNode = stageRef.current;
    if (!tr || !wrap || !stageNode || previewMode || tr.nodes().length === 0) {
      setDeletePos(null);
      return;
    }
    // Local top-left of the selection box (rotates with the object), not AABB.
    const pt = tr.getAbsoluteTransform().point({ x: 0, y: 0 });
    const stageBox = stageNode.container().getBoundingClientRect();
    const wrapBox = wrap.getBoundingClientRect();
    const half = HANDLE_CSS / 2;
    setDeletePos({
      left: stageBox.left - wrapBox.left + pt.x - half,
      top: stageBox.top - wrapBox.top + pt.y - half,
      rotation: tr.rotation(),
    });
  }, [previewMode]);

  useEffect(() => {
    const tr = trRef.current;
    if (!tr) return;
    const node = selectedId ? nodeByIdRef.current.get(selectedId) ?? null : null;
    selectedRef.current = node;
    if (node && !previewMode) {
      tr.nodes([node]);
      tr.getLayer()?.batchDraw();
      // next frame so transformer has laid out anchors
      requestAnimationFrame(() => syncDeletePos());
    } else {
      tr.nodes([]);
      setDeletePos(null);
    }
  }, [selectedId, objects, activeView, previewMode, syncDeletePos, stage.width, stage.height]);

  useEffect(() => {
    const tr = trRef.current;
    const node = selectedRef.current;
    if (!tr || !node || previewMode || !selectedId) return;

    const onMove = () => syncDeletePos();
    tr.on('transform', onMove);
    tr.on('transformend', onMove);
    node.on('dragmove', onMove);
    node.on('dragend', onMove);
    return () => {
      tr.off('transform', onMove);
      tr.off('transformend', onMove);
      node.off('dragmove', onMove);
      node.off('dragend', onMove);
    };
  }, [selectedId, objects, activeView, previewMode, syncDeletePos]);

  const styleAnchor = useCallback((anchor: Konva.Rect) => {
    // Konva names anchors as "rotater _anchor", "bottom-right _anchor", …
    const name = anchor.name();
    const pack = icons.current ?? getSelectionIcons(HANDLE_CSS);
    const size = HANDLE_CSS;
    const isRotater = name.includes('rotater');
    const isScale = name.includes('bottom-right');
    if (!isRotater && !isScale) return;

    anchor.width(size);
    anchor.height(size);
    anchor.offsetX(size / 2);
    anchor.offsetY(size / 2);
    anchor.cornerRadius(size / 2);
    anchor.strokeEnabled(false);
    anchor.fillEnabled(true);
    anchor.fillPriority('pattern');
    const img = isRotater ? pack.rotate : pack.scale;
    anchor.fillPatternImage(img);
    anchor.fillPatternRepeat('no-repeat');
    anchor.fillPatternScaleX(size / img.width);
    anchor.fillPatternScaleY(size / img.height);
    anchor.fillPatternOffsetX(0);
    anchor.fillPatternOffsetY(0);
    anchor.hitStrokeWidth(14);
  }, []);

  const dpr = Math.min(2, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);

  return (
    <div className="ox-canvas-wrap" ref={wrapRef}>
      <Stage
        ref={(node) => {
          stageRef.current = node;
        }}
        width={stage.width}
        height={stage.height}
        pixelRatio={dpr}
        onMouseDown={(e) => {
          if (e.target === e.target.getStage()) select(null);
        }}
        onTap={(e) => {
          if (e.target === e.target.getStage()) select(null);
        }}
      >
        <Layer listening={false}>
          {mockup && (
            <KonvaImage image={mockup} width={stage.width} height={stage.height} />
          )}
        </Layer>
        <Layer>
          <Group x={areaX} y={areaY}>
            {/* Clip art to print area (La Tostadora-style). Transformer stays outside so handles remain visible. */}
            <Group clip={{ x: 0, y: 0, width: areaW, height: areaH }}>
              {objects.map((obj) => {
                const x = obj.x * displayPxPerMm;
                const y = obj.y * displayPxPerMm;
                const w = obj.width * displayPxPerMm;
                const h = obj.height * displayPxPerMm;
                if (obj.type === 'text') {
                  return (
                    <TextObject
                      key={obj.id}
                      selected={obj.id === selectedId}
                      setNode={(n) => registerNode(obj.id, n)}
                      x={x}
                      y={y}
                      width={w}
                      height={h}
                      text={obj.content}
                      fontFamily={obj.fontFamily}
                      fontSize={obj.fontSize * displayPxPerMm}
                      fontStyle={String(obj.fontWeight)}
                      fill={obj.color}
                      align={obj.align}
                      curve={(obj.curve ?? 'none') as TextCurve}
                      curveRadius={(obj.curveRadius ?? 80) * displayPxPerMm}
                      curveGlyphs={obj.curveGlyphs}
                      mmToPx={displayPxPerMm}
                      opacity={obj.opacity}
                      rotation={obj.rotation}
                      locked={!!obj.locked}
                      previewMode={previewMode}
                      onSelect={() => select(obj.id)}
                      onDragEnd={(nx, ny) =>
                        updateObject(obj.id, {
                          x: nx / displayPxPerMm,
                          y: ny / displayPxPerMm,
                        })
                      }
                      onTransformEnd={(nx, ny, nw, nh, fontSizePx, rot, scale) =>
                        updateObject(obj.id, {
                          x: nx / displayPxPerMm,
                          y: ny / displayPxPerMm,
                          width: Math.max(2, nw / displayPxPerMm),
                          height: Math.max(2, nh / displayPxPerMm),
                          fontSize: Math.max(2, fontSizePx / displayPxPerMm),
                          rotation: rot,
                          ...((obj.curve ?? 'none') !== 'none'
                            ? {
                                curveRadius: Math.max(
                                  8,
                                  (obj.curveRadius ?? 80) * scale,
                                ),
                              }
                            : {}),
                        })
                      }
                    />
                  );
                }
                return (
                  <ImageObject
                    key={obj.id}
                    assetId={obj.assetId}
                    galleryCode={obj.galleryCode}
                    mask={obj.mask}
                    maskScale={obj.maskScale ?? 1}
                    maskOffsetX={obj.maskOffsetX ?? 0}
                    maskOffsetY={obj.maskOffsetY ?? 0}
                    filter={resolveImageFilter(obj.effects?.filter, obj.effects?.grayscale)}
                    x={x}
                    y={y}
                    width={w}
                    height={h}
                    rotation={obj.rotation}
                    opacity={obj.opacity}
                    selected={obj.id === selectedId}
                    onSelect={() => select(obj.id)}
                    setNode={(n) => registerNode(obj.id, n)}
                    onDragEnd={(nx, ny) =>
                      updateObject(obj.id, {
                        x: nx / displayPxPerMm,
                        y: ny / displayPxPerMm,
                      })
                    }
                    onTransformEnd={(nx, ny, nw, nh, rot) =>
                      updateObject(obj.id, {
                        x: nx / displayPxPerMm,
                        y: ny / displayPxPerMm,
                        width: nw / displayPxPerMm,
                        height: nh / displayPxPerMm,
                        rotation: rot,
                      })
                    }
                    onNaturalSize={(nw, nh) => {
                      // Unmasked gallery (and legacy squashed boxes): restore native aspect
                      // by contain-fitting into the print area — never stretch the bitmap.
                      if ((obj.mask ?? 'none') !== 'none') return;
                      if (!aspectMismatch(obj.width, obj.height, nw, nh)) return;
                      const pa = area;
                      if (!pa) return;
                      const { widthMm, heightMm } = fitImageToPrintArea(
                        nw,
                        nh,
                        pa.width,
                        pa.height,
                      );
                      updateObject(obj.id, {
                        width: widthMm,
                        height: heightMm,
                        x: (pa.width - widthMm) / 2,
                        y: (pa.height - heightMm) / 2,
                      });
                    }}
                    previewMode={previewMode}
                  />
                );
              })}
            </Group>
            {!previewMode && (
              <Transformer
                ref={trRef}
                rotateEnabled
                rotateLineVisible={false}
                rotateAnchorOffset={28}
                keepRatio
                shouldOverdrawWholeArea
                enabledAnchors={['bottom-right']}
                borderStroke="#3b82f6"
                borderStrokeWidth={1.5}
                anchorFill="#fff"
                anchorStroke="#3b82f6"
                anchorStrokeWidth={0}
                anchorSize={HANDLE_CSS}
                anchorCornerRadius={HANDLE_CSS / 2}
                anchorStyleFunc={styleAnchor}
                boundBoxFunc={(oldBox, newBox) => {
                  if (newBox.width < 10 || newBox.height < 10) return oldBox;
                  return newBox;
                }}
              />
            )}
          </Group>
          {showGuides && !previewMode && (
            <>
              <Rect
                x={areaX}
                y={areaY}
                width={areaW}
                height={areaH}
                stroke="#888"
                dash={[6, 4]}
                strokeWidth={1}
                listening={false}
              />
              <Rect
                x={areaX + bleed}
                y={areaY + bleed}
                width={areaW - bleed * 2}
                height={areaH - bleed * 2}
                stroke="#bbb"
                dash={[2, 3]}
                strokeWidth={1}
                listening={false}
              />
            </>
          )}
        </Layer>
      </Stage>
      {selectedId && !previewMode && deletePos && (
        <button
          type="button"
          className="ox-selection-delete"
          style={{
            left: deletePos.left,
            top: deletePos.top,
            transform: `rotate(${deletePos.rotation}deg)`,
          }}
          onClick={(e) => {
            e.stopPropagation();
            deleteSelected();
          }}
          title="Borrar"
          aria-label="Borrar"
        />
      )}
    </div>
  );
}

function TextObject(props: {
  selected: boolean;
  setNode: (n: Konva.Node | null) => void;
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
  fontFamily: string;
  fontSize: number;
  fontStyle: string;
  fill: string;
  align: string;
  curve: TextCurve;
  curveRadius: number;
  curveGlyphs?: CurveGlyph[];
  /** mm → display px */
  mmToPx: number;
  opacity: number;
  rotation: number;
  locked: boolean;
  previewMode: boolean;
  onSelect: () => void;
  onDragEnd: (x: number, y: number) => void;
  onTransformEnd: (
    x: number,
    y: number,
    w: number,
    h: number,
    fontSizePx: number,
    rot: number,
    scale: number,
  ) => void;
}) {
  const dragging = useRef(false);
  const [pos, setPos] = useState({ x: props.x, y: props.y });
  const curved = props.curve === 'arc-up' || props.curve === 'arc-down';
  const konvaFontStyle = cssFontWeight(props.fontStyle);

  useEffect(() => {
    if (!dragging.current) setPos({ x: props.x, y: props.y });
  }, [props.x, props.y]);

  // Canvas always lays out in display px and rasterizes. Baked mm glyphs are for SVG/print.
  const layout = useMemo(() => {
    if (!curved) return null;
    const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    const ctx = canvas?.getContext('2d') ?? null;
    if (ctx) ctx.font = `${konvaFontStyle} ${props.fontSize}px ${props.fontFamily}`;
    const live = layoutCurvedText({
      content: props.text,
      fontSize: props.fontSize,
      curve: props.curve === 'arc-down' ? 'arc-down' : 'arc-up',
      curveRadius: props.curveRadius,
      measureChar: ctx ? (ch) => ctx.measureText(ch).width : undefined,
    });
    // Fit into the object's transformer box
    const scaleX = props.width / Math.max(live.width, 0.01);
    const scaleY = props.height / Math.max(live.height, 0.01);
    const s = Math.min(scaleX, scaleY);
    const ox = (props.width - live.width * s) / 2;
    const oy = (props.height - live.height * s) / 2;
    return {
      glyphs: live.glyphs.map((g) => ({
        char: g.char,
        x: ox + g.x * s,
        y: oy + g.y * s,
        rotation: g.rotation,
      })),
      width: props.width,
      height: props.height,
      radius: live.radius * s,
    };
  }, [
    curved,
    props.width,
    props.height,
    props.curveRadius,
    props.fontSize,
    props.fontFamily,
    props.curve,
    props.text,
    konvaFontStyle,
  ]);

  const curveCacheKey = useMemo(() => {
    if (!layout) return '';
    const g = layout.glyphs.map((x) => `${x.char}:${x.x.toFixed(1)}:${x.y.toFixed(1)}:${x.rotation.toFixed(1)}`).join('|');
    return `${props.text}|${props.curve}|${props.fontSize}|${props.fill}|${g}`;
  }, [layout, props.text, props.curve, props.fontSize, props.fill]);

  const curveImg = useCurvedTextImage({
    enabled: curved && !!layout,
    glyphs: layout?.glyphs ?? [],
    width: props.width,
    height: props.height,
    fontSize: props.fontSize,
    fontFamily: props.fontFamily,
    fontStyle: konvaFontStyle,
    fill: props.fill,
    cacheKey: curveCacheKey,
  });

  const canDrag = props.selected && !props.locked && !props.previewMode;

  const c = centerOf(pos.x, pos.y, props.width, props.height);

  const dragHandlers = {
    onDragStart: () => {
      dragging.current = true;
      props.onSelect();
    },
    onDragMove: (e: Konva.KonvaEventObject<DragEvent>) => {
      setPos(topLeftOf(e.target.x(), e.target.y(), props.width, props.height));
    },
    onDragEnd: (e: Konva.KonvaEventObject<DragEvent>) => {
      const tl = topLeftOf(e.target.x(), e.target.y(), props.width, props.height);
      dragging.current = false;
      setPos(tl);
      props.onDragEnd(tl.x, tl.y);
    },
  };

  if (curved && layout) {
    const drawW = props.width;
    const drawH = props.height;

    return (
      <Group
        ref={(node) => props.setNode(node)}
        x={c.x}
        y={c.y}
        offsetX={drawW / 2}
        offsetY={drawH / 2}
        opacity={props.opacity ?? 1}
        rotation={props.rotation}
        draggable={canDrag}
        onClick={props.onSelect}
        onTap={props.onSelect}
        onDragStart={dragHandlers.onDragStart}
        onDragMove={dragHandlers.onDragMove}
        onDragEnd={dragHandlers.onDragEnd}
        onTransformEnd={(e) => {
          const node = e.target;
          const sx = node.scaleX();
          const sy = node.scaleY();
          const sc = Math.max(0.05, (Math.abs(sx) + Math.abs(sy)) / 2);
          const newW = Math.max(10, drawW * sc);
          const newH = Math.max(10, drawH * sc);
          const newFontSize = Math.max(4, props.fontSize * sc);
          node.scaleX(1);
          node.scaleY(1);
          node.offsetX(newW / 2);
          node.offsetY(newH / 2);
          const tl = topLeftOf(node.x(), node.y(), newW, newH);
          setPos(tl);
          props.onTransformEnd(tl.x, tl.y, newW, newH, newFontSize, node.rotation(), sc);
        }}
      >
        <Rect width={drawW} height={drawH} fill="rgba(0,0,0,0)" />
        {curveImg && (
          <KonvaImage
            image={curveImg}
            width={drawW}
            height={drawH}
            listening={false}
            perfectDrawEnabled={false}
          />
        )}
      </Group>
    );
  }

  // Plain text: Group + hit Rect (same as curved/image) so mobile touch targets the box,
  // not glyph outlines — and only the selected node is draggable (avoids image stealing drag).
  return (
    <Group
      ref={(node) => props.setNode(node)}
      x={c.x}
      y={c.y}
      offsetX={props.width / 2}
      offsetY={props.height / 2}
      opacity={props.opacity}
      rotation={props.rotation}
      draggable={canDrag}
      onClick={props.onSelect}
      onTap={props.onSelect}
      {...dragHandlers}
      onTransformEnd={(e) => {
        const node = e.target;
        const sx = node.scaleX();
        const sy = node.scaleY();
        const scale = Math.max(0.05, (Math.abs(sx) + Math.abs(sy)) / 2);
        const newW = Math.max(10, props.width * scale);
        const newH = Math.max(10, props.height * scale);
        const newFontSize = Math.max(4, props.fontSize * scale);
        node.scaleX(1);
        node.scaleY(1);
        node.offsetX(newW / 2);
        node.offsetY(newH / 2);
        const tl = topLeftOf(node.x(), node.y(), newW, newH);
        setPos(tl);
        props.onTransformEnd(tl.x, tl.y, newW, newH, newFontSize, node.rotation(), scale);
      }}
    >
      <Rect width={props.width} height={props.height} fill="rgba(0,0,0,0)" />
      <Text
        width={props.width}
        height={props.height}
        text={props.text}
        fontFamily={props.fontFamily}
        fontSize={props.fontSize}
        fontStyle={konvaFontStyle}
        fill={props.fill}
        align={props.align}
        verticalAlign="middle"
        wrap="none"
        listening={false}
        perfectDrawEnabled={false}
      />
    </Group>
  );
}

function ImageObject(props: {
  assetId?: string;
  galleryCode?: string;
  mask?: string;
  maskScale?: number;
  maskOffsetX?: number;
  maskOffsetY?: number;
  filter?: ImageFilter;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  opacity: number;
  selected: boolean;
  previewMode: boolean;
  onSelect: () => void;
  setNode: (n: Konva.Node | null) => void;
  onDragEnd: (x: number, y: number) => void;
  onTransformEnd: (x: number, y: number, w: number, h: number, rot: number) => void;
  /** Called once when natural bitmap size is known (px). */
  onNaturalSize?: (widthPx: number, heightPx: number) => void;
}) {
  const [img, setImg] = useState<CanvasImageSource | null>(null);
  const [natural, setNatural] = useState({ w: 1, h: 1 });
  const maskUrl = maskImageUrl(props.mask);
  const maskImg = useHtmlImage(maskUrl);
  const dragging = useRef(false);
  const naturalReported = useRef(false);
  const [pos, setPos] = useState({ x: props.x, y: props.y });
  const filter = props.filter ?? 'none';

  useEffect(() => {
    if (!dragging.current) setPos({ x: props.x, y: props.y });
  }, [props.x, props.y]);

  useEffect(() => {
    naturalReported.current = false;
  }, [props.assetId, props.galleryCode]);

  useEffect(() => {
    const host = window.document.querySelector('ox-designer') as HTMLElement | null;
    const base = host?.getAttribute('api-base') ?? 'http://localhost:4010';
    const storeId = host?.getAttribute('store-id') || 'ox';
    let cancelled = false;
    let objectUrl: string | null = null;

    async function loadSrc(src: string) {
      let imgSrc = src;
      try {
        const imgRes = await fetch(src, { mode: 'cors', credentials: 'omit' });
        if (imgRes.ok) {
          const blob = await imgRes.blob();
          objectUrl = URL.createObjectURL(blob);
          imgSrc = objectUrl;
        }
      } catch {
        /* fall back to direct URL */
      }
      if (cancelled) return;
      const i = new window.Image();
      i.onload = () => {
        if (cancelled) return;
        const nw = i.naturalWidth || i.width || 1;
        const nh = i.naturalHeight || i.height || 1;
        setNatural({ w: nw, h: nh });
        if (!naturalReported.current && props.onNaturalSize) {
          naturalReported.current = true;
          props.onNaturalSize(nw, nh);
        }
        try {
          setImg(bakeImageFilter(i, filter));
        } catch {
          setImg(i);
        }
      };
      i.onerror = () => {
        if (!cancelled) setImg(null);
      };
      i.src = imgSrc;
    }

    void (async () => {
      try {
        if (props.galleryCode) {
          const metaUrl = `${base.replace(/\/$/, '')}/gallery/items/${encodeURIComponent(props.galleryCode)}?storeId=${encodeURIComponent(storeId)}`;
          const res = await fetch(metaUrl);
          const data = res.ok ? await res.json() : null;
          const src = data?.artworkUrl as string | undefined;
          if (src && !cancelled) await loadSrc(src);
          return;
        }
        if (!props.assetId) return;
        const metaUrl = `${base.replace(/\/$/, '')}/assets/${props.assetId}`;
        const headers: Record<string, string> = {
          ...oxAuthHeaders(),
          'X-Ox-Store': storeId,
        };
        const res = await fetch(metaUrl, { headers });
        const data = res.ok ? await res.json() : null;
        if (cancelled || !data) return;
        const src = data.proxyUrl || data.thumbUrl;
        if (src) await loadSrc(src);
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [props.assetId, props.galleryCode, filter]);

  const c = centerOf(pos.x, pos.y, props.width, props.height);
  const hasMask = !!maskImg;
  // Mask PNGs + Edit Image stage are square; never stretch the shape to the photo box.
  const maskBox = hasMask ? squareMaskBox(props.width, props.height) : null;
  const layout = hasMask && maskBox
    ? (() => {
        const inner = maskPhotoLayout(
          maskBox.side,
          maskBox.side,
          natural.w,
          natural.h,
          props.maskScale ?? 1,
          props.maskOffsetX ?? 0,
          props.maskOffsetY ?? 0,
        );
        return {
          x: inner.x + maskBox.offsetX,
          y: inner.y + maskBox.offsetY,
          width: inner.width,
          height: inner.height,
        };
      })()
    : { x: 0, y: 0, width: props.width, height: props.height };

  // Bake mask off-layer. Konva `destination-in` composites against the whole Layer
  // canvas and erases siblings drawn earlier (e.g. text under a masked image).
  const [maskedImg, setMaskedImg] = useState<HTMLCanvasElement | null>(null);
  useEffect(() => {
    if (!img || !maskImg || !maskBox || props.width < 1 || props.height < 1) {
      setMaskedImg(null);
      return;
    }
    const dpr = Math.min(2, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil(props.width * dpr));
    canvas.height = Math.max(1, Math.ceil(props.height * dpr));
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setMaskedImg(null);
      return;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, props.width, props.height);
    ctx.drawImage(img, layout.x, layout.y, layout.width, layout.height);
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(maskImg, maskBox.offsetX, maskBox.offsetY, maskBox.side, maskBox.side);
    ctx.globalCompositeOperation = 'source-over';
    setMaskedImg(canvas);
  }, [
    img,
    maskImg,
    maskBox?.side,
    maskBox?.offsetX,
    maskBox?.offsetY,
    props.width,
    props.height,
    layout.x,
    layout.y,
    layout.width,
    layout.height,
  ]);

  const displayImg = hasMask ? maskedImg : img;
  const canDrag = props.selected && !props.previewMode;

  return (
    <Group
      ref={(node) => props.setNode(node)}
      x={c.x}
      y={c.y}
      offsetX={props.width / 2}
      offsetY={props.height / 2}
      rotation={props.rotation}
      draggable={canDrag}
      onClick={props.onSelect}
      onTap={props.onSelect}
      onDragStart={() => {
        dragging.current = true;
        props.onSelect();
      }}
      onDragMove={(e) => {
        setPos(topLeftOf(e.target.x(), e.target.y(), props.width, props.height));
      }}
      onDragEnd={(e) => {
        const tl = topLeftOf(e.target.x(), e.target.y(), props.width, props.height);
        dragging.current = false;
        setPos(tl);
        props.onDragEnd(tl.x, tl.y);
      }}
      onTransformEnd={(e) => {
        const node = e.target;
        const sx = node.scaleX();
        const sy = node.scaleY();
        const newW = Math.max(10, props.width * sx);
        const newH = Math.max(10, props.height * sy);
        node.scaleX(1);
        node.scaleY(1);
        node.offsetX(newW / 2);
        node.offsetY(newH / 2);
        const tl = topLeftOf(node.x(), node.y(), newW, newH);
        setPos(tl);
        props.onTransformEnd(tl.x, tl.y, newW, newH, node.rotation());
      }}
    >
      <KonvaImage
        image={displayImg ?? undefined}
        x={0}
        y={0}
        width={props.width}
        height={props.height}
        opacity={props.opacity}
        fill={displayImg ? undefined : '#ccc'}
        perfectDrawEnabled={false}
      />
    </Group>
  );
}
